/**
 * Direct Bedrock Agent Core Service
 * 
 * This service calls the Bedrock Agent Core endpoint directly using OAuth tokens,
 * bypassing the API Gateway and Lambda functions for prompt generation.
 * Uses apiClient for all HTTP requests with authentication.
 */

import apiClient from '@/utils/apiClient';
import {
    type Configuration,
    type DemoData,
    SimpleAgentConfigSchema,
    SimpleDemoConfigSchema,
    SimpleExamplesConfigSchema
} from '@demo-portal/shared';

// Bedrock Agent Core configuration
const BEDROCK_AGENT_CORE_ENDPOINT_URL = "https://bedrock-agentcore.us-east-1.amazonaws.com";
const AGENT_ARN = import.meta.env.VITE_BEDROCK_AGENT_CORE_ARN;
const GUARDRAIL_ID = import.meta.env.VITE_GUARDRAIL_ID;

if (AGENT_ARN === undefined) {
    throw new Error('VITE_BEDROCK_AGENT_CORE_ARN is not defined');
}

if (GUARDRAIL_ID === undefined) {
    throw new Error('VITE_GUARDRAIL_ID is not defined');
}

// Prompt payload structure
export interface PromptPayload {
    prompt: string;
    guardrail_id: string;
}


// Bedrock Agent Core service error class
export class BedrockAgentCoreServiceError extends Error {
    constructor(
        message: string,
        public code?: string,
        public statusCode?: number,
        public details?: string
    ) {
        super(message);
        this.name = 'BedrockAgentCoreServiceError';
    }

    getUserFriendlyMessage(): string {
        switch (this.code) {
            case 'AUTHENTICATION_ERROR':
                return 'Authentication failed. Please sign in again.';
            case 'RATE_LIMIT_EXCEEDED':
                return 'Too many requests. Please wait a moment and try again.';
            case 'INVALID_REQUEST':
                return 'Invalid request. Please check your input and try again.';
            case 'SERVICE_UNAVAILABLE':
                return 'AI service is temporarily unavailable. Please try again later.';
            default:
                return this.message || 'An unexpected error occurred with the AI service.';
        }
    }
}

/**
 * Direct Bedrock Agent Core Service Class
 * Uses apiClient for all HTTP requests with authentication
 */
class BedrockAgentCoreService {

    /**
     * Make request to Bedrock Agent Core endpoint using apiClient
     */
    private async makeRequest(payload: PromptPayload): Promise<Configuration> {
        try {
            console.debug('🤖 Bedrock Agent Core Service: Making direct request via apiClient');

            // Construct the full endpoint URL
            const escapedAgentArn = encodeURIComponent(AGENT_ARN);
            const url = `${BEDROCK_AGENT_CORE_ENDPOINT_URL}/runtimes/${escapedAgentArn}/invocations?qualifier=DEFAULT`;

            console.debug('🔄 Making request to Bedrock Agent Core:', {
                url: url.replace(AGENT_ARN, '[AGENT_ARN]'), // Hide ARN in logs
                payloadSize: JSON.stringify(payload).length
            });

            // Use apiClient to make the request (handles auth, headers, etc.)
            const response = await apiClient.post<any>(url, payload);

            // Handle API client errors
            if (response.error) {
                console.error('❌ Bedrock Agent Core Service: API request failed:', response.error);
                throw new BedrockAgentCoreServiceError(
                    response.error,
                    this.getErrorCode(response.status),
                    response.status
                );
            }

            if (!response.data) {
                throw new BedrockAgentCoreServiceError(
                    'No response data received from Bedrock Agent Core',
                    'INVALID_RESPONSE'
                );
            }

            console.debug('✅ Bedrock Agent Core Service: Response received successfully');

            // Validate and parse the response
            return this.validateAndParseResponse(response.data);

        } catch (error) {
            console.error('❌ Bedrock Agent Core Service: Request failed:', error);

            if (error instanceof BedrockAgentCoreServiceError) {
                throw error;
            }

            throw new BedrockAgentCoreServiceError(
                `Failed to communicate with Bedrock Agent Core: ${error instanceof Error ? error.message : 'Unknown error'}`,
                'UNKNOWN_ERROR'
            );
        }
    }

    /**
     * Validate and parse agent response (handles new response format)
     */
    private validateAndParseResponse(response: any): Configuration {
        try {
            console.debug('🔍 Bedrock Agent Core Service: Parsing and validating response');

            // Check if response is empty
            if (!response || typeof response !== 'object') {
                throw new Error('Invalid response from Bedrock Agent Core');
            }

            // Handle the new response format: { success: true, use_case_processed: "...", configuration: { config: {...} } }
            let configData = response;

            // Check for new response format with success and configuration fields
            if (response.success !== undefined && response.configuration) {
                console.log('Detected new response format with success and configuration fields');

                // Check if the request was successful
                if (!response.success) {
                    throw new Error('Agent processing failed');
                }

                // Log the processed use case if available
                if (response.use_case_processed) {
                    console.log('Use case processed:', response.use_case_processed);
                }

                // Check if configuration contains the legacy content structure
                if (response.configuration.content && Array.isArray(response.configuration.content)) {
                    console.log('New response format contains legacy content structure, extracting JSON');

                    // Extract JSON from the content array
                    const content = response.configuration.content;
                    if (content.length > 0 && content[0].text) {
                        let jsonText = content[0].text;

                        // Remove markdown code block markers if present
                        jsonText = jsonText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();

                        try {
                            configData = JSON.parse(jsonText);
                            console.log('Successfully extracted JSON from new response format with content');
                        } catch (innerParseError) {
                            console.error('Failed to parse inner JSON from new format:', innerParseError);
                            throw new Error('Invalid JSON in agent configuration content');
                        }
                    } else {
                        throw new Error('Invalid configuration content structure in new format');
                    }
                } else {
                    // Direct configuration data
                    configData = response.configuration;
                }
            }
            // Handle legacy wrapped response structure with configuration.content
            else if (response.configuration && response.configuration.content) {
                console.log('Detected legacy wrapped response structure, extracting configuration');

                // Extract JSON from the content array
                const content = response.configuration.content;
                if (Array.isArray(content) && content.length > 0 && content[0].text) {
                    let jsonText = content[0].text;

                    // Remove markdown code block markers if present
                    jsonText = jsonText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();

                    try {
                        configData = JSON.parse(jsonText);
                        console.log('Successfully extracted JSON from wrapped response');
                    } catch (innerParseError) {
                        console.error('Failed to parse inner JSON:', innerParseError);
                        throw new Error('Invalid JSON in agent configuration content');
                    }
                } else {
                    throw new Error('Invalid configuration content structure');
                }
            }

            // Validate the extracted configuration data
            if (!configData.config || !configData.agents || !configData.examples) {
                const missingFields = [];
                if (!configData.config) missingFields.push('config');
                if (!configData.agents) missingFields.push('agents');
                if (!configData.examples) missingFields.push('examples');
                throw new Error(`Agent response missing required fields: ${missingFields.join(', ')}`);
            }

            // Basic validation of nested structures
            if (typeof configData.config !== 'object') {
                throw new Error('Config must be an object');
            }

            if (!Array.isArray(configData.agents)) {
                throw new Error('Agents must be an array');
            }

            if (!Array.isArray(configData.examples)) {
                throw new Error('Examples must be an array');
            }

            console.debug('✅ Bedrock Agent Core Service: Response validation successful');

            // Validate against schemas with detailed error reporting (non-blocking)
            let config, agents, examples;

            try {
                config = SimpleDemoConfigSchema.parse(configData.config);
                console.debug('✅ Config schema validation passed');
            } catch (error) {
                console.error('⚠️ Config validation error (continuing anyway):', error);
                config = configData.config; // Use raw data if validation fails
            }

            try {
                agents = SimpleAgentConfigSchema.parse(configData.agents);
                console.debug('✅ Agents schema validation passed');
            } catch (error) {
                console.error('⚠️ Agents validation error (continuing anyway):', error);
                agents = configData.agents; // Use raw data if validation fails
            }

            try {
                examples = SimpleExamplesConfigSchema.parse(configData.examples);
                console.debug('✅ Examples schema validation passed');
            } catch (error) {
                console.error('⚠️ Examples validation error (continuing anyway):', error);
                examples = configData.examples; // Use raw data if validation fails
            }

            return {
                config,
                agents,
                examples,
                welcomeMessage: configData.welcomeMessage,
                responseTemplates: configData.responseTemplates,
            };

        } catch (error) {
            console.error('❌ Bedrock Agent Core Service: Failed to parse response:', error);
            throw new BedrockAgentCoreServiceError(
                `Response validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                'INVALID_RESPONSE'
            );
        }
    }

    /**
     * Generate demo configuration from business use case prompt
     */
    async generateDemoFromPrompt(useCase: string): Promise<DemoData> {
        try {
            console.debug('🤖 Bedrock Agent Core Service: Generating demo from prompt');

            // Validate input
            if (!useCase || useCase.trim().length === 0) {
                throw new BedrockAgentCoreServiceError(
                    'Use case prompt cannot be empty',
                    'INVALID_REQUEST',
                    400
                );
            }

            if (useCase.length > 10000) {
                throw new BedrockAgentCoreServiceError(
                    'Prompt is too long (maximum 10,000 characters)',
                    'INVALID_REQUEST',
                    400
                );
            }

            // Format payload
            const payload: PromptPayload = {
                prompt: useCase.trim(),
                guardrail_id: GUARDRAIL_ID
            };

            // Make request to Bedrock Agent Core
            const response = await this.makeRequest(payload);

            console.debug('✅ Bedrock Agent Core Service: Demo generated successfully');
            return response;

        } catch (error) {
            console.error('❌ Bedrock Agent Core Service: Failed to generate demo:', error);

            if (error instanceof BedrockAgentCoreServiceError) {
                throw error;
            }

            throw new BedrockAgentCoreServiceError(
                `Failed to generate demo configuration: ${error instanceof Error ? error.message : 'Unknown error'}`,
                'UNKNOWN_ERROR'
            );
        }
    }

    /**
     * Generate demo configuration with streaming support (placeholder for future implementation)
     */
    async generateDemoStream(
        useCase: string,
        _onChunk?: (chunk: string) => void,
        onComplete?: (data: DemoData) => void,
        onError?: (error: string) => void
    ): Promise<DemoData> {
        try {
            // For now, use the regular method and simulate streaming
            const result = await this.generateDemoFromPrompt(useCase);

            if (onComplete) {
                onComplete(result);
            }

            return result;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            if (onError) {
                onError(errorMessage);
            }
            throw error;
        }
    }

    /**
     * Get error code from HTTP status
     */
    private getErrorCode(status: number): string {
        switch (status) {
            case 400:
                return 'INVALID_REQUEST';
            case 401:
            case 403:
                return 'AUTHENTICATION_ERROR';
            case 429:
                return 'RATE_LIMIT_EXCEEDED';
            case 503:
                return 'SERVICE_UNAVAILABLE';
            default:
                return 'UNKNOWN_ERROR';
        }
    }

    /**
     * Check if the service is ready to use
     */
    async isReady(): Promise<boolean> {
        const authStatus = await apiClient.checkAuthenticationStatus();
        return authStatus.isConfigured && authStatus.hasToken;
    }

    /**
     * Get service configuration
     */
    getConfig() {
        return {
            endpoint: BEDROCK_AGENT_CORE_ENDPOINT_URL,
            agentArn: AGENT_ARN,
            isReady: this.isReady(),
        };
    }
}

// Export singleton instance
export const bedrockAgentCoreService = new BedrockAgentCoreService();
export default bedrockAgentCoreService;